import githubSupportHero from '../assets/work/github-support/hero.webp';
import githubSupportResearch from '../assets/work/github-support/research.png';
import githubSupportSitemap from '../assets/work/github-support/sitemap.png';
import githubSupportBefore from '../assets/work/github-support/before.png';
import githubSupportAfter from '../assets/work/github-support/after.png';
import githubSupportSiteAnalysis1 from '../assets/work/github-support/site-analysis-1.png';
import githubSupportSiteAnalysis2 from '../assets/work/github-support/site-analysis-2.png';
import githubSupportSolution1 from '../assets/work/github-support/solution-1.png';
import githubSupportSolution2 from '../assets/work/github-support/solution-2.png';
import githubSupportSolution3 from '../assets/work/github-support/solution-3.png';
import githubSupportSolution4 from '../assets/work/github-support/solution-4.png';
import githubSupportSolution5 from '../assets/work/github-support/solution-5.png';
import githubSupportSolution6 from '../assets/work/github-support/solution-6.png';
import githubSupportSolution7 from '../assets/work/github-support/solution-7.png';

import githubSuccessHero from '../assets/work/github-success/hero.webp';
import githubSuccessContext from '../assets/work/github-success/context.png';
import githubSuccessDashboard from '../assets/work/github-success/dashboard.png';
import githubSuccessAdmin from '../assets/work/github-success/admin-essentials.png';

import vmartHero from '../assets/work/vmart/hero.png';
import vmartSitemap from '../assets/work/vmart/sitemap.png';
import vmartFlow from '../assets/work/vmart/flow.png';
import vmartElements from '../assets/work/vmart/elements.png';

import kingHero from '../assets/work/king-metals/hero.png';
import kingBefore from '../assets/work/king-metals/before.png';
import kingAfter from '../assets/work/king-metals/after.png';
import kingInsights from '../assets/work/king-metals/insights.png';

import clapperHero from '../assets/work/clapper/hero.webp';
import clapperProblem from '../assets/work/clapper/problem.png';
import clapperUser from '../assets/work/clapper/user.png';
import clapperSitemap from '../assets/work/clapper/sitemap.png';

import transportProHero from '../assets/work/transport-pro/hero.png';
import transportProProblem from '../assets/work/transport-pro/problem.png';
import transportProStrategy from '../assets/work/transport-pro/strategy.png';
import transportProSolution1 from '../assets/work/transport-pro/solution1.png';
import transportProSolution2 from '../assets/work/transport-pro/solution2.png';

export const workProjects = {
  'github-support': {
    slug: 'github-support',
    navLabel: 'GitHub Support',
    client: 'GitHub',
    category: 'Developer Tools · Enterprise SaaS',
    role: 'Product Design & Frontend Engineering',
    timeline: '5 months',
    stat: '37% faster loads · 91% fewer dead clicks',
    heroImage: githubSupportHero,
    seoTitle: 'GitHub Support Portal Redesign Case Study',
    seoDescription:
      'How Pegasus UX rebuilt the GitHub support portal to cut ticket volume, speed up page loads by 37%, and reduce dead clicks by 91%.',
    headline: [
      { text: 'Rebuilding self-service support', tone: 'muted', italic: true },
      { text: 'for 100M+ developers.', tone: 'gradient' },
    ],
    subtitle:
      "GitHub's support portal was buckling under ticket volume. We rebuilt it as a self-service-first experience, giving every developer a faster path to an answer without filing a ticket.",
    sections: [
      {
        id: 'problem',
        eyebrow: 'The Problem',
        title: 'Ticket volume was outpacing the team that answers it.',
        paragraphs: [
          "GitHub's support portal was receiving more ticket submissions than its support team could sustainably handle, and hiring alone wasn't a scalable answer. The portal itself was built on aging Rails infrastructure that made it hard to iterate on navigation or content.",
          'We started by auditing the existing site and interviewing users. The pattern was consistent: when developers hit a snag, they skipped the support portal entirely and searched third-party forums instead, because our own navigation and search made it faster to look elsewhere than to find an answer on GitHub.',
        ],
        image: githubSupportResearch,
        imageCaption: 'User research surfaced how often developers bypassed the portal for third-party forums.',
      },
      {
        id: 'approach',
        eyebrow: 'Our Approach',
        title: 'A sitemap built around how developers actually search.',
        paragraphs: [
          'We ran a full audit of the existing portal to see exactly where structure broke down and search fell short, then restructured the information architecture around real support topics instead of internal product boundaries. We added a unified global search across all content and surfaced community discussion and popular articles directly in the navigation.',
        ],
        images: [
          {
            src: githubSupportSiteAnalysis1,
            caption: 'Auditing the existing portal: content scattered across teams with no unified structure.',
          },
          {
            src: githubSupportSiteAnalysis2,
            caption: 'Key findings from the audit that shaped the new information architecture.',
          },
          {
            src: githubSupportSitemap,
            caption: 'The redesigned sitemap, organized around user pain points rather than internal team structure.',
          },
        ],
      },
      {
        id: 'solution',
        eyebrow: 'The Solution',
        title: 'A faster, more resilient portal built on GitHub Primer.',
        paragraphs: [
          "We led the design and frontend rebuild of the portal in React on GitHub's own Primer design system, which gave us accessibility and consistency out of the box while we focused on the experience layer: enhanced search, richer multimedia content, and fully responsive layouts across devices.",
        ],
        images: [
          { src: githubSupportSolution1 },
          { src: githubSupportSolution2 },
          { src: githubSupportSolution3 },
          { src: githubSupportSolution4 },
          { src: githubSupportSolution5 },
          { src: githubSupportSolution6 },
          { src: githubSupportSolution7 },
        ],
      },
      {
        id: 'outcome',
        eyebrow: 'The Outcome',
        title: 'Version 2 shipped, and the numbers moved fast.',
        paragraphs: [
          'Within months of launch, ticket submissions dropped 14%, dead clicks fell 91%, and average page load time improved by 37% following the move from Ruby to React. The portal is live today at support.github.com.',
        ],
        image: githubSupportAfter,
        beforeImage: githubSupportBefore,
        imageCaption: 'Before and after: the redesigned support portal homepage.',
      },
    ],
  },

  'github-success': {
    slug: 'github-success',
    navLabel: 'GitHub Success',
    client: 'GitHub · Copilot Enterprise',
    category: 'Enterprise Analytics · AI',
    role: 'Product Design & Research',
    timeline: '5 months',
    stat: '87% of admins wanted org-wide access',
    heroImage: githubSuccessHero,
    seoTitle: 'GitHub Enterprise Success Portal Case Study',
    seoDescription:
      'How Pegasus UX designed a self-serve enterprise analytics dashboard for GitHub Enterprise Admins, paired with Copilot AI guidance.',
    headline: [
      { text: 'Turning internal health data', tone: 'muted', italic: true },
      { text: 'into a self-serve dashboard.', tone: 'gradient' },
    ],
    subtitle:
      'Enterprise Admins were flying blind on their own usage data, relying on support tickets to justify spend to leadership. We designed a self-serve analytics experience, paired with Copilot AI guidance.',
    sections: [
      {
        id: 'context',
        eyebrow: 'Context',
        title: 'GitHub already had the data. Admins just never saw it.',
        paragraphs: [
          "GitHub tracks a detailed internal health score for every enterprise organization: adoption, pull request activity, and deeper diagnostic signals that go well beyond the basic billing and usage numbers customers can see themselves. Support teams use this data to proactively guide Enterprise Admins toward gaps in how they're using GitHub's products. Customers never had direct access to it.",
        ],
        image: githubSuccessContext,
      },
      {
        id: 'problem',
        eyebrow: 'The Problem',
        title: 'Two questions shaped the entire engagement.',
        paragraphs: [
          "First: how do we surface this valuable internal data to Enterprise Admins in a way they can actually consume and act on? Second: how do we bring Copilot AI into the experience so it adds genuine value, rather than feeling like it's dictating how a customer should run their organization?",
        ],
      },
      {
        id: 'research',
        eyebrow: 'Research',
        title: 'Admins think in spend, seats, and justification.',
        paragraphs: [
          'We interviewed Enterprise Admins directly. Almost all of them were also the lead developer on their team. When we asked how they measure their organization\'s success, the answers converged on the same themes: not overspending, pipeline efficiency, and proving that purchased seats and products were actually being used. Every admin we spoke with placed a high priority on justifying spend on consumable products, like GitHub Advanced Security, to upper management.',
        ],
      },
      {
        id: 'solution',
        eyebrow: 'The Solution',
        title: 'A new "Admin Essentials" home, and a dashboard that drills down.',
        paragraphs: [
          "We introduced Admin Essentials, a dedicated navigation section that renders only for Enterprise Admins, giving them a direct entry point to this new experience. Rather than a home page listing 10-15 dense tables, we validated with users that they preferred a focused dashboard, billed usage, seat usage, active developers, and a product breakdown, with the ability to drill into any metric for detail, filter by date range, and export reports.",
          "We also A/B tested two approaches to Copilot: an open-ended chat assistant versus a more prescriptive panel of AI-generated suggestions. Admins pushed back hard on the prescriptive version, saying it felt like GitHub was defining productivity for them. We chose the lighter, open-ended chat experience instead, a simpler build and a better fit for how admins wanted to work.",
        ],
        image: githubSuccessDashboard,
        imageCaption: 'The Enterprise Admin dashboard: billed usage, seat usage, and product breakdown at a glance.',
      },
      {
        id: 'outcome',
        eyebrow: 'The Outcome',
        title: 'Validated by the people who\'d actually use it.',
        paragraphs: [
          "Every admin we tested the prototype with said the experience added real value. 87% said it should extend beyond admins to their entire developer team, citing a desire for organization-wide transparency into productivity. Admins consistently valued having hard metrics they could point to when justifying product spend to leadership.",
        ],
        image: githubSuccessAdmin,
        imageCaption: 'Admin Essentials: the new entry point to enterprise health data.',
      },
    ],
  },

  vmart: {
    slug: 'vmart',
    navLabel: 'V-Mart',
    client: 'V-Mart',
    category: 'Consumer · E-Commerce',
    role: 'UX Research & Product Design',
    timeline: '3 months',
    stat: 'Concept validated, desktop & mobile',
    heroImage: vmartHero,
    seoTitle: 'V-Mart Specialty Grocery E-Commerce Case Study',
    seoDescription:
      'How Pegasus UX designed a responsive e-commerce concept for an underserved specialty grocery market, from persona research through hi-fidelity UI.',
    headline: [
      { text: 'Designing discovery for a niche,', tone: 'muted', italic: true },
      { text: 'underserved market.', tone: 'gradient' },
    ],
    subtitle:
      'Specialty grocery has no Amazon. We ran a research-to-prototype engagement for a responsive Vietnamese grocery ordering experience, then carried the underlying navigation pattern into later enterprise commerce work.',
    sections: [
      {
        id: 'problem',
        eyebrow: 'The Problem',
        title: 'Specialty ingredients, nowhere convenient to buy them.',
        paragraphs: [
          'Vietnamese grocery markets are scarce in both rural and urban areas, making it difficult for shoppers to find specific ingredients close to home. We were asked to design a responsive experience, usable across age groups, that made browsing and ordering Vietnamese groceries online genuinely convenient.',
        ],
      },
      {
        id: 'approach',
        eyebrow: 'Our Approach',
        title: 'Research first, then a persona to design against.',
        paragraphs: [
          'We researched how people were already ordering groceries online, including habits accelerated by the pandemic, and the frustrations that came with existing tools. That research became a persona, Billy, representing the core shopper this experience needed to serve. From there, we mapped a simplified sitemap and the two dominant paths a shopper takes: arriving with something specific in mind, or browsing until something catches their eye.',
        ],
        image: vmartSitemap,
        imageCaption: 'The simplified sitemap, reduced to the essentials of how shoppers actually navigate.',
      },
      {
        id: 'solution',
        eyebrow: 'The Solution',
        title: 'From low-fidelity flows to a refined UI system.',
        paragraphs: [
          'We built out both user flows in low-fidelity wireframes for desktop and mobile, then layered in a UI component system on top. Usability feedback was positive with only minor iteration needed: adjusting type scale, softening a heavy border treatment to a lighter gray, and condensing the footer into two clear sections.',
        ],
        image: vmartFlow,
        imageCaption: 'Core user flows: targeted purchase versus open browsing.',
      },
      {
        id: 'outcome',
        eyebrow: 'The Outcome',
        title: 'A validated pattern that outlived the project.',
        paragraphs: [
          "The concept was validated and build-ready across devices. More notably, the category-first navigation and filtering pattern we developed here became the template we later adapted for King Architectural Metals' 40,000-SKU catalog, proof that a pattern designed for a niche consumer market can scale directly into enterprise commerce.",
        ],
        image: vmartElements,
        imageCaption: 'UI elements built on top of the low-fidelity foundation.',
      },
    ],
  },

  'king-metals': {
    slug: 'king-metals',
    navLabel: 'King Metals',
    client: 'King Architectural Metals',
    category: 'E-Commerce · Enterprise Retail',
    role: 'Product Design & Frontend Development',
    timeline: '10 months',
    stat: '+19% sales · -12% support calls',
    heroImage: kingHero,
    seoTitle: 'King Architectural Metals E-Commerce Redesign Case Study',
    seoDescription:
      'How Pegasus UX modernized a 40,000-SKU e-commerce catalog off legacy infrastructure, growing sales 19% and cutting support calls 12%.',
    headline: [
      { text: 'Modernizing a 40,000-SKU catalog', tone: 'muted', italic: true },
      { text: 'stuck on a decades-old stack.', tone: 'gradient' },
    ],
    subtitle:
      'A legacy storefront was costing King Architectural Metals sales and pushing customers to competitors. We rebuilt the purchasing experience end to end.',
    sections: [
      {
        id: 'problem',
        eyebrow: 'The Problem',
        title: 'Over 40,000 products, and no good way to find them.',
        paragraphs: [
          "King Architectural Metals ran a wide catalog of metal products on a website built decades earlier on a static LAMP stack. That infrastructure made it nearly impossible to evolve the interface as customer expectations shifted. Customers told us navigation felt like it was built around the volume of inventory, not around how they actually wanted to shop, and many were quietly migrating to competitor sites.",
        ],
        image: kingBefore,
        imageCaption: 'The legacy interface: dense, dated, and difficult to navigate at this catalog size.',
      },
      {
        id: 'goals',
        eyebrow: 'Goals',
        title: 'Make a 40,000-product catalog feel effortless.',
        paragraphs: [
          "Navigation, product cards, and filtering were working against customers instead of for them. Online buying experiences elsewhere had become nearly frictionless in the years since King's original site launched, and our goal was to bring that same feeling to a catalog an order of magnitude larger than most e-commerce sites handle well.",
        ],
        image: kingInsights,
      },
      {
        id: 'solution',
        eyebrow: 'The Solution',
        title: 'A funnel that gets more specific with every click.',
        paragraphs: [
          "We rebuilt the storefront in React with a new component library and a refreshed brand identity, then designed each component to guide buyers through a progressively specialized path. A buyer visiting the Fences category, for example, sees Fences, Fence Decorations, and Fence Hardware; clicking any one of those lands on a pre-filtered product list for that exact category. A global search with the same filtering sits at the top of every page as a fallback for anyone searching directly.",
        ],
        image: kingAfter,
        imageCaption: 'The rebuilt purchasing flow: category-first navigation with pre-applied filters.',
      },
      {
        id: 'outcome',
        eyebrow: 'The Outcome',
        title: 'Sales up, support calls down, and still climbing.',
        paragraphs: [
          "The redesigned experience drove a 19% increase in sales within the first months after launch, with continued month-over-month growth. Inbound support calls dropped 12% as customers found what they needed without help, and engagement and retention, tracked through Google Tag Manager, have trended up ever since.",
        ],
      },
    ],
  },

  clapper: {
    slug: 'clapper',
    navLabel: 'Clapper',
    client: 'Clapper',
    category: 'Social · Consumer Tech',
    role: 'UX Research Lead',
    timeline: '7 months',
    stat: '$3M raised following the engagement',
    heroImage: clapperHero,
    seoTitle: 'Clapper Social Platform Research Case Study',
    seoDescription:
      'How a Pegasus UX-led research engagement shaped the product direction for Clapper, a free-expression social platform, ahead of a $3M funding round.',
    headline: [
      { text: 'De-risking a social platform bet', tone: 'muted', italic: true },
      { text: 'with research before a line of code.', tone: 'gradient' },
    ],
    subtitle:
      "Clapper wanted to build a platform where creators wouldn't get arbitrarily banned. We led the research that shaped the product direction, and helped make the case for its seed round.",
    sections: [
      {
        id: 'problem',
        eyebrow: 'The Problem',
        title: 'Creators were losing accounts over opinions, not violations.',
        paragraphs: [
          'Content creators on incumbent platforms were being suspended or permanently removed for expressing opinions on current events, decisions often driven by shifting advertiser preferences rather than clear policy violations. Entire topics were effectively off-limits, chilling the kind of open discourse Clapper wanted to build a platform around.',
        ],
        image: clapperProblem,
      },
      {
        id: 'approach',
        eyebrow: 'Our Approach',
        title: 'Research centered on users, not advertisers.',
        paragraphs: [
          'We led the initial research initiative for the platform, deliberately designing it around user needs rather than the commercial pressures that shape moderation on incumbent platforms. We mapped how creators think about expression, risk, and community, and used that to define what an open forum would actually need to deliver.',
        ],
        image: clapperUser,
        imageCaption: 'User research into how creators experience moderation and risk on existing platforms.',
      },
      {
        id: 'solution',
        eyebrow: 'The Solution',
        title: 'A roadmap built around open discourse at scale.',
        paragraphs: [
          "Our findings shaped a product direction centered on live, co-hosted discussion formats to encourage healthy exchange around current events, a paid partnership program to incentivize creators to build on the platform, and a longer-term 'Town Hall' model for large-scale public discourse. Together, this became a roadmap the engineering team could build directly against.",
        ],
        image: clapperSitemap,
        imageCaption: 'The information architecture that grew out of the research findings.',
      },
      {
        id: 'outcome',
        eyebrow: 'The Outcome',
        title: 'Research that helped close the round.',
        paragraphs: [
          'The research and the product narrative it produced became part of the story Clapper told investors. Shortly after the engagement wrapped, the company closed a $3M seed round, capital it has used to build out the platform and scale operations.',
        ],
      },
    ],
  },
  'transport-pro': {
    slug: 'transport-pro',
    navLabel: 'Transport Pro',
    client: 'Transport Pro',
    category: 'Logistics · Enterprise SaaS',
    role: 'UX Strategy & Design Leadership',
    timeline: '4 months',
    stat: '2x user retention · +20% driver-load matching',
    heroImage: transportProHero,
    seoTitle: 'Transport Pro Logistics Platform Redesign Case Study',
    seoDescription:
      'How Pegasus UX led a four-phase UX strategy to modernize a legacy logistics platform, doubling user retention and improving driver-load matching by 20%.',
    headline: [
      { text: 'Modernizing the UI behind', tone: 'muted', italic: true },
      { text: 'freight communication.', tone: 'gradient' },
    ],
    subtitle:
      "Transport Pro's platform was running on a codebase from the early 2000s. We led the strategy and design team through a four-phase overhaul that doubled user retention and sharpened driver-load matching by 20%.",
    sections: [
      {
        id: 'problem',
        eyebrow: 'The Problem',
        title: 'An outdated, over-clicked UI was costing new business.',
        paragraphs: [
          "Transport Pro's platform connected drivers, load managers, and brokers through a dense web of data and tables, but brokers and account executives were struggling to quickly find available drivers. The interface was overly click-heavy, with key actions buried in a complex, nested navigation header. That friction was making it harder to win new business, since prospective customers judged the product on how outdated the UI felt.",
        ],
        image: transportProProblem,
        imageCaption: 'The legacy interface: dense data tables with no clear path to what brokers needed most.',
      },
      {
        id: 'approach',
        eyebrow: 'Our Approach',
        title: 'We pushed back on more features, and chose focus instead.',
        paragraphs: [
          "Leadership's first instinct was to add more: more suggestions, more surfaced data, more automation for matching loads to drivers. We redirected the team toward a different bet, that clear, confident communication between drivers, load managers, and brokers mattered more than raw feature volume. Guided by user feedback and a team that was starting to feel stretched thin by an overextended feature set, we built a four-phase strategy: streamline the existing feature set, strengthen support outside of scheduled meetings, consolidate overlapping tools, and roll out the redefined platform in a way existing users could adapt to without disruption.",
        ],
        image: transportProStrategy,
        imageCaption: 'The four-phase strategy briefed to leadership and the engineering team.',
      },
      {
        id: 'solution',
        eyebrow: 'The Solution',
        title: 'From a passive homepage to an active, user-centric one.',
        paragraphs: [
          "We led the product and content designers on our team through a shift from a static, passive homepage to a dynamically active interface, one that paired continuous, personalized suggestions with substantive feedback. Rather than optimizing purely for metrics, we balanced immediate feedback with anticipatory tips and a mix of qualitative and quantitative insight, giving users a clearer path to improving how they communicated, not just more data to sift through.",
        ],
        image: transportProSolution2,
        imageCaption: 'The redesigned homepage: personalized, real-time suggestions in place of static tables.',
      },
      {
        id: 'outcome',
        eyebrow: 'The Outcome',
        title: 'Retention doubled, and matching got measurably sharper.',
        paragraphs: [
          "The redesigned platform drove a two-fold increase in user retention and a 20%+ improvement in users' ability to match drivers with available loads, alongside a 10%+ increase in companies choosing Transport Pro as their logistics SaaS of choice. User feedback consistently cited a smoother journey from A to Z, with far less time lost navigating dense data or losing track of why they opened the app in the first place.",
        ],
        image: transportProSolution1,
        imageCaption: 'The rebuilt experience: personalized suggestions paired with clear, actionable feedback.',
      },
    ],
  },
};

export const workOrder = ['github-support', 'github-success', 'king-metals', 'clapper', 'transport-pro'];
